/**
 * Фото крупно — как `PinViewer`: оверлей поверх папки, Esc закрывает, стрелки
 * листают (не пока курсор в поле ввода).
 *
 * Показывается `view_path` — JPG для просмотра (у HEIC — сконвертированный).
 * Его нет — конвертация не удалась: заглушка с причиной и ссылка на оригинал,
 * как прислали (браузер HEIC не покажет, но скачать даст).
 *
 * Справа — все упоминания фото: одно и то же фото могли прислать дважды, и
 * каждое сообщение — своя строка со ссылкой в Telegram.
 */
import { ChevronLeft, ChevronRight, ExternalLink, ImageOff, X } from "lucide-react";
import { useEffect } from "react";

import { plural } from "@/lib/library";
import { mediaUrl, when } from "@/lib/media";
import type { Photo } from "@/lib/photos";

export function PhotoViewer({
  photos,
  index,
  onIndex,
  onClose,
}: {
  photos: Photo[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
}) {
  const photo = photos[index];
  const count = photos.length;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "SELECT" || el.tagName === "TEXTAREA")) return;
      if (event.key === "ArrowRight" && index < count - 1) onIndex(index + 1);
      if (event.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, count, onIndex, onClose]);

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch bg-black/75 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Фото крупно"
      onClick={onClose}
      data-annot="фото крупно"
    >
      <button
        type="button"
        className="absolute top-3 right-[352px] z-10 grid size-9 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
        onClick={onClose}
        title="Закрыть (Esc)"
      >
        <X size={18} />
      </button>

      <div className="flex min-w-0 flex-1 items-center justify-center gap-2 p-4">
        <NavButton dir="prev" disabled={index === 0} onClick={() => onIndex(index - 1)} />
        <div className="flex h-full min-w-0 flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
          {photo.view_path ? (
            <img
              key={photo.id}
              src={mediaUrl(photo.view_path)}
              alt={photo.mentions[0]?.file_name ?? "фото"}
              className="max-h-[calc(100vh-2rem)] max-w-full rounded-[var(--radius-card)] object-contain shadow-2xl"
            />
          ) : (
            <div className="grid justify-items-center gap-2 text-center text-[13px] text-white/70">
              <ImageOff size={28} />
              <div>{photo.error ?? "Просмотра нет."}</div>
              <a
                href={mediaUrl(photo.asset_path)}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-white underline-offset-2 hover:underline"
              >
                <ExternalLink size={12} />
                оригинал
              </a>
            </div>
          )}
        </div>
        <NavButton dir="next" disabled={index >= count - 1} onClick={() => onIndex(index + 1)} />
      </div>

      <aside
        className="flex w-[340px] flex-none flex-col gap-3 overflow-y-auto border-l border-border bg-bg p-4 text-[12.5px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11.5px] text-fg-faint">
          {index + 1} из {count} · ← → листать, Esc — закрыть
        </div>

        <div className="grid gap-2" data-annot="упоминания фото">
          <div className="text-[12.5px] font-medium text-fg">
            {photo.mentions.length > 1
              ? `Присылали ${photo.mentions.length} ${plural(photo.mentions.length, "раз", "раза", "раз")}`
              : "Из чата"}
          </div>
          {photo.mentions.length ? (
            <ul className="grid gap-1.5">
              {photo.mentions.map((m) => (
                <li key={m.message_url} className="text-fg-muted">
                  {m.user_name} · {m.location_short ?? "—"} · <span className="tnum">{when(m.date)}</span> ·{" "}
                  {m.file_name ?? "фото"} ·{" "}
                  <a
                    href={m.message_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent-text hover:underline"
                  >
                    открыть в Telegram
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-fg-faint">
              {photo.crm_media_id !== null ? `из CRM · медиа ${photo.crm_media_id}` : "Сообщений с этим фото нет."}
            </div>
          )}
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-t border-border pt-3">
          <dt className="text-fg-faint">папка</dt>
          <dd className="text-fg-muted">
            {photo.link_kind === "manual" ? "закреплено человеком" : "поставила кластеризация"}
          </dd>
          {photo.width && photo.height ? (
            <>
              <dt className="text-fg-faint">размер</dt>
              <dd className="tnum text-fg-muted">
                {photo.width}×{photo.height}
              </dd>
            </>
          ) : null}
          {photo.error ? (
            <>
              <dt className="text-fg-faint">ошибка</dt>
              <dd className="text-err">{photo.error}</dd>
            </>
          ) : null}
        </dl>

        <a
          href={mediaUrl(photo.asset_path)}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex w-fit items-center gap-1 text-accent-text hover:underline"
        >
          <ExternalLink size={12} />
          оригинал, как прислали
        </a>
      </aside>
    </div>
  );
}

function NavButton({ dir, disabled, onClick }: { dir: "prev" | "next"; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={dir === "prev" ? "Предыдущее (←)" : "Следующее (→)"}
      className="grid size-10 flex-none place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:invisible"
    >
      {dir === "prev" ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
    </button>
  );
}
